import React from 'react';
import HarbourIcon from './HarbourIcon';
import HarbourTextLink from './HarbourTextLink';
import HarbourDocumentRow from './HarbourDocumentRow';
import {
  ADD_LOCKED_UNTIL_CONSENT,
  CAP_REACHED,
  DOCUMENTS_LOAD_FAILED,
  DOCUMENTS_NONE_SENT,
  DOCUMENTS_NONE_YET,
  DOCUMENTS_SENT,
  DOCUMENTS_SENT_NONE,
  DOCUMENTS_UNTIL_SENT,
  capLine,
} from '../../utils/referralDocumentPortal';
import './harbour.css';
import './harbourDocuments.css';

/** The muted lines under the documents: the cap (parent or carer only) and when adding closes. */
export function documentsHelperLines(summary) {
  if (!summary) return [];
  const count = (summary.documents || []).length;
  if (summary.sent) return [count ? DOCUMENTS_SENT : DOCUMENTS_SENT_NONE];
  return [capLine(summary), DOCUMENTS_UNTIL_SENT].filter(Boolean);
}

/** Whether the "Add a document" action is offered: open, and under the cap. */
export function canAddDocument(summary) {
  return Boolean(summary && summary.canAdd && !summary.sent);
}

/** At the cap, the add action is replaced by this plain message (TDI-579). */
export function atCap(summary) {
  return Boolean(summary && !summary.sent && summary.addRefusal === 'cap_reached');
}

/** Before the parent has agreed to the referral, the add action shows but is
 * locked (TDI-786, R-046) — the server refuses the upload too. */
export function addLockedForConsent(summary) {
  return Boolean(summary && !summary.sent && summary.addRefusal === 'consent_not_given');
}

/*
 * Block · Documents (Figma 4209:294556, D-04 and D-05): inside the Referral
 * pathway card, built like Block · School forms. One grey surface, a 64px
 * header with "Add a document" on the right, a divider, then each document
 * as a plain line and the helper as a muted line. After the referral is
 * sent the block stays, read-only, with the sent line (TDI-580).
 *
 * The same block serves the SENCO record and the parent or carer's desktop
 * record. Each side only ever gets its own documents from the server.
 */
export default function HarbourDocumentsBlock({
  summary,
  loading = false,
  error = null,
  onRetry,
  onAdd,
  onRemove,
}) {
  if (summary && summary.onReferralPathway === false) return null;
  const documents = (summary && summary.documents) || [];
  const showAdd = canAddDocument(summary) && typeof onAdd === 'function';
  const lockedAdd = addLockedForConsent(summary);
  // While adding is locked, the cap and until-sent lines would contradict
  // the lock, so the locked line stands alone.
  const helper = lockedAdd ? [] : documentsHelperLines(summary).filter((line) => line !== CAP_REACHED);
  return (
    <section className="harbour-doc-block" aria-label="Documents">
      <div className="harbour-doc-block__head">
        <h3 className="harbour-doc-block__title">Documents</h3>
        {showAdd && (
          <HarbourTextLink leadingIcon="plus" size="standard" onClick={onAdd} className="harbour-doc-block__add">
            Add a document
          </HarbourTextLink>
        )}
        {lockedAdd && (
          <span className="harbour-doc-block__add harbour-doc-block__add--locked" aria-disabled="true">
            <HarbourIcon name="lock" size={20} title="Locked" />
            Add a document
          </span>
        )}
      </div>
      <div className="harbour-doc-block__divider" aria-hidden="true" />
      <div className="harbour-doc-block__list">
        {loading && !summary ? (
          <>
            <div className="harbour-skeleton harbour-doc-block__skeleton animate-pulse" aria-hidden="true" />
            <p className="sr-only" role="status">Loading documents</p>
          </>
        ) : error && !summary ? (
          <div className="harbour-doc-block__line harbour-doc-block__line--error">
            <span>{DOCUMENTS_LOAD_FAILED}</span>
            {onRetry && <HarbourTextLink onClick={() => onRetry()}>Try again</HarbourTextLink>}
          </div>
        ) : (
          <>
            {documents.length > 0 ? (
              <ul className="harbour-doc-block__rows">
                {documents.map((d) => (
                  <HarbourDocumentRow key={d.id} document={d} onRemove={summary && !summary.sent ? onRemove : null} />
                ))}
              </ul>
            ) : (
              <p className="harbour-doc-block__line">{summary && summary.sent ? DOCUMENTS_NONE_SENT : DOCUMENTS_NONE_YET}</p>
            )}
            {atCap(summary) && <p className="harbour-doc-block__line harbour-doc-block__line--cap">{CAP_REACHED}</p>}
            {lockedAdd && <p className="harbour-doc-block__line">{ADD_LOCKED_UNTIL_CONSENT}</p>}
            {helper.map((line) => (
              <p key={line} className="harbour-doc-block__line">{line}</p>
            ))}
          </>
        )}
      </div>
    </section>
  );
}
